import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { createTheme, CssBaseline, ThemeProvider } from '@mui/material';
import { ColorModeContext } from './Contexts/ThemeContext';
import { LanguageProvider, useLang } from './Contexts/LanguageContext';
import RtlProvider from './Contexts/RtlProvider';
import { PersistGate } from 'redux-persist/integration/react';
import { persistor, store } from './Redux';
import { Provider } from 'react-redux';
import Loading from './components/Loading';

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const ThemedApp = () => {
  const [mode, setMode] = React.useState<'light' | 'dark'>('light');
  const { isRTL, lang } = useLang();

  const colorMode = React.useMemo(() => ({
    toggleColorMode: () => {
      setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
    },
  }), []);

  const theme = React.useMemo(
    () =>
      createTheme({
        direction: isRTL ? 'rtl' : 'ltr',
        palette: {
          mode,
          primary: { main: "#00314f" }
        },
      }),
    [mode, isRTL]
  );

  // Sync <html> dir + lang attributes
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [isRTL, lang]);

  return (
    <RtlProvider isRTL={isRTL}>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </RtlProvider>
  );
};

const Main = () => (
  <Provider store={store}>
    <PersistGate loading={<Loading />} persistor={persistor}>
      <LanguageProvider>
        <ThemedApp />
      </LanguageProvider>
    </PersistGate>
  </Provider>
);

root.render(<Main />);
